import { getCookie } from 'api/utils/getCookie'
import config from 'config'
import { LogContextType, OctaneLogger } from 'integrations/datadog/OctaneLogger'

import type {
    AppSyncRealtimeConfiguration,
    RealtimeChannel,
    RealtimeConfiguration,
    RealtimeTelemetryEvent,
    RealtimeWorkerInputMessage,
    RealtimeWorkerOutputMessage,
    RealtimeWorkerPortMessage,
} from './types'

export type RealtimeMessageListener = (
    message: RealtimeWorkerOutputMessage,
) => void
type PortInitializer = (port: MessagePort) => void | (() => void)

export interface RealtimeChannelRegistration {
    dispose: () => void
    signal: AbortSignal
}

export interface RealtimeClientDetachContext {
    reason: 'pagehide'
    pagePersisted: boolean
}

interface BrowserRealtimeSubscription {
    readonly owners: Set<object>
    outcome?: RealtimeSubscriptionOutcome
}

interface BrowserRealtimeSubscriptionOwnership {
    subscription: BrowserRealtimeSubscription
    release: () => void
}

type RealtimeSubscriptionOutcome = Extract<
    RealtimeWorkerOutputMessage,
    { type: 'subscription_ready' | 'subscription_denied' }
>

const REALTIME_MOCK_ENABLED = process.env.REACT_ENABLE_REALTIME_MOCK === 'true'
const REALTIME_MOCK_MODE_STORAGE_KEY = 'octane.realtimeEvents.mode'
const REALTIME_LOG_MESSAGE = 'Realtime SharedWorker event'
const reportedUnavailableReasons = new Set<RealtimeUnavailableReason>()

type RealtimeUnavailableReason =
    | 'feature_disabled'
    | 'missing_api_base_url'
    | 'missing_realtime_endpoint'
    | 'missing_csrf_token'
    | 'shared_worker_unsupported'

type RealtimeConfigurationResult =
    | { configuration: RealtimeConfiguration }
    | { unavailableReason: RealtimeUnavailableReason }

export interface RealtimeEventsClient {
    addMessageListener: (listener: RealtimeMessageListener) => () => void
    registerChannelListener: (
        channel: RealtimeChannel,
        listener: RealtimeMessageListener,
    ) => RealtimeChannelRegistration
}

const isMockModeRequested = (): boolean => {
    if (!REALTIME_MOCK_ENABLED) return false

    try {
        return (
            window.localStorage.getItem(REALTIME_MOCK_MODE_STORAGE_KEY) ===
            'mock'
        )
    } catch {
        return false
    }
}

const logRealtimeTelemetry = (
    telemetry: RealtimeTelemetryEvent,
    error?: Error,
): void => {
    const context: LogContextType = {
        realtime: {
            event: telemetry.event,
            ...telemetry.context,
        },
    }

    switch (telemetry.level) {
        case 'error':
            OctaneLogger.error(REALTIME_LOG_MESSAGE, context, error)

            return
        case 'warn':
            OctaneLogger.warn(REALTIME_LOG_MESSAGE, context, error)

            return
        case 'info':
            OctaneLogger.info(REALTIME_LOG_MESSAGE, context)
    }
}

const reportRealtimeUnavailable = (
    reason: RealtimeUnavailableReason,
    transport?: 'appsync' | 'mock',
): void => {
    if (reason === 'feature_disabled' || reportedUnavailableReasons.has(reason))
        return

    reportedUnavailableReasons.add(reason)

    logRealtimeTelemetry({
        event: 'worker_unavailable',
        level: 'warn',
        context: { reason, transport },
    })
}

const getRealtimeConfiguration = (): RealtimeConfigurationResult => {
    if (isMockModeRequested()) return { configuration: { mode: 'mock' } }

    if (config.REALTIME_EVENTS_ENABLED !== true) {
        return { unavailableReason: 'feature_disabled' }
    }

    if (!config.API_BASE_URL) {
        return { unavailableReason: 'missing_api_base_url' }
    }

    if (!config.REALTIME_EVENTS_REALTIME_ENDPOINT) {
        return { unavailableReason: 'missing_realtime_endpoint' }
    }

    const csrfToken = getCookie('csrftoken')

    if (!csrfToken) {
        return { unavailableReason: 'missing_csrf_token' }
    }

    return {
        configuration: {
            mode: 'appsync',
            losApiBaseUrl: config.API_BASE_URL,
            appsyncRealtimeEndpoint: config.REALTIME_EVENTS_REALTIME_ENDPOINT,
            csrfToken,
        },
    }
}

const installWorkerErrorLogging = (
    worker: SharedWorker,
    transport: 'appsync' | 'mock',
): (() => void) | undefined => {
    if (typeof worker.addEventListener !== 'function') return undefined

    const handleError = (event: Event): void => {
        const errorEvent = event as ErrorEvent
        const error = errorEvent.error

        logRealtimeTelemetry(
            {
                event: 'worker_runtime_error',
                level: 'error',
                context: {
                    transport,
                    message: errorEvent.message || undefined,
                },
            },
            error instanceof Error ? error : undefined,
        )
    }

    worker.addEventListener('error', handleError)

    return () => worker.removeEventListener('error', handleError)
}

class BrowserRealtimeEventsClient implements RealtimeEventsClient {
    private readonly listeners = new Set<RealtimeMessageListener>()
    private readonly registrationAbortControllers = new Set<AbortController>()
    private readonly subscriptions = new Map<
        RealtimeChannel,
        BrowserRealtimeSubscription
    >()
    private isDetached = false
    private workerId?: string
    private readonly detachPortInitializer?: () => void
    private readonly detachWorkerErrorLogging?: () => void

    constructor(
        private readonly port: MessagePort,
        private readonly transport: 'appsync' | 'mock',
        initializePort?: PortInitializer,
        detachWorkerErrorLogging?: () => void,
    ) {
        const detachPortInitializer = initializePort?.(port)

        if (detachPortInitializer) {
            this.detachPortInitializer = detachPortInitializer
        }

        this.detachWorkerErrorLogging = detachWorkerErrorLogging

        this.port.addEventListener('message', this.handleMessage)
        this.port.onmessageerror = this.handleMessageError
        this.port.start()
    }

    configureAndConnect(configuration: AppSyncRealtimeConfiguration): void {
        this.postMessage({ type: 'configure', configuration })
        this.postMessage({ type: 'connect' })
    }

    addMessageListener(listener: RealtimeMessageListener): () => void {
        if (this.isDetached) return () => undefined

        this.listeners.add(listener)

        return () => this.listeners.delete(listener)
    }

    registerChannelListener(
        channel: RealtimeChannel,
        listener: RealtimeMessageListener,
    ): RealtimeChannelRegistration {
        const abortController = new AbortController()

        if (this.isDetached) {
            abortController.abort()

            return {
                dispose: () => undefined,
                signal: abortController.signal,
            }
        }

        const removeMessageListener = this.addMessageListener(listener)
        const { subscription, release } = this.acquireSubscription(channel)
        let isDisposed = false

        this.registrationAbortControllers.add(abortController)

        this.replaySubscriptionOutcome(
            subscription,
            listener,
            abortController.signal,
        )

        return {
            dispose: () => {
                if (isDisposed) return

                isDisposed = true
                abortController.abort()
                this.registrationAbortControllers.delete(abortController)
                removeMessageListener()
                release()
            },
            signal: abortController.signal,
        }
    }

    private acquireSubscription(
        channel: RealtimeChannel,
    ): BrowserRealtimeSubscriptionOwnership {
        let subscription = this.subscriptions.get(channel)

        if (!subscription) {
            subscription = { owners: new Set() }
            this.subscriptions.set(channel, subscription)
            this.postMessage({ type: 'subscribe', channel })
        }

        const owner = {}

        subscription.owners.add(owner)
        let isReleased = false

        return {
            subscription,
            release: () => {
                if (isReleased) return

                isReleased = true

                if (this.subscriptions.get(channel) !== subscription) return

                subscription.owners.delete(owner)

                if (subscription.owners.size > 0) return

                this.subscriptions.delete(channel)
                this.postMessage({ type: 'unsubscribe', channel })
            },
        }
    }

    private replaySubscriptionOutcome(
        subscription: BrowserRealtimeSubscription,
        listener: RealtimeMessageListener,
        signal: AbortSignal,
    ): void {
        const outcome = subscription.outcome

        if (!outcome) return

        queueMicrotask(() => {
            if (
                signal.aborted ||
                this.isDetached ||
                this.subscriptions.get(outcome.channel) !== subscription ||
                subscription.outcome !== outcome
            ) {
                return
            }

            listener(outcome)
        })
    }

    logout(): void {
        this.invalidateRegistrations()
        this.subscriptions.clear()
        this.postMessage({ type: 'logout' })
    }

    detach(): void {
        if (this.isDetached) return

        this.postMessage({ type: 'detach' })
        this.isDetached = true
        this.invalidateRegistrations()
        this.subscriptions.clear()
        this.listeners.clear()
        this.detachPortInitializer?.()
        this.detachWorkerErrorLogging?.()
        this.port.removeEventListener('message', this.handleMessage)

        if (this.port.onmessageerror === this.handleMessageError) {
            this.port.onmessageerror = null
        }
    }

    private readonly handleMessage = (
        event: MessageEvent<RealtimeWorkerPortMessage>,
    ): void => {
        if (event.data.type === 'telemetry') {
            const workerId = event.data.telemetry.context?.worker_id

            if (typeof workerId === 'string') this.workerId = workerId

            logRealtimeTelemetry(event.data.telemetry)

            return
        }

        if (
            event.data.type === 'subscription_ready' ||
            event.data.type === 'subscription_denied'
        ) {
            const subscription = this.subscriptions.get(event.data.channel)

            if (subscription) subscription.outcome = event.data
        } else if (event.data.type === 'disconnected') {
            for (const subscription of this.subscriptions.values()) {
                subscription.outcome = undefined
            }
        }

        if (
            event.data.type === 'session_expired' ||
            (event.data.type === 'disconnected' &&
                event.data.reason === 'logout')
        ) {
            this.invalidateRegistrations()
            this.subscriptions.clear()
        }

        for (const listener of [...this.listeners]) listener(event.data)
    }

    private readonly handleMessageError = (): void => {
        logRealtimeTelemetry({
            event: 'port_message_error',
            level: 'warn',
            context: {
                recoverable: true,
                transport: this.transport,
                worker_id: this.workerId,
            },
        })
    }

    private invalidateRegistrations(): void {
        for (const controller of this.registrationAbortControllers) {
            controller.abort()
        }

        this.registrationAbortControllers.clear()
    }

    private postMessage(message: RealtimeWorkerInputMessage): void {
        if (!this.isDetached) this.port.postMessage(message)
    }
}

let realtimeEventsClient: BrowserRealtimeEventsClient | undefined

let realtimeEventsClientPromise:
    | Promise<BrowserRealtimeEventsClient | undefined>
    | undefined

let clientGeneration = 0

export const getRealtimeEventsClient = async (): Promise<
    RealtimeEventsClient | undefined
> => {
    if (realtimeEventsClient) return realtimeEventsClient

    if (realtimeEventsClientPromise) return realtimeEventsClientPromise

    const configurationResult = getRealtimeConfiguration()

    if ('unavailableReason' in configurationResult) {
        reportRealtimeUnavailable(configurationResult.unavailableReason)

        return undefined
    }

    if (typeof window.SharedWorker !== 'function') {
        reportRealtimeUnavailable(
            'shared_worker_unsupported',
            configurationResult.configuration.mode,
        )

        return undefined
    }

    const { configuration } = configurationResult
    const generation = clientGeneration

    const createClient = async (): Promise<
        BrowserRealtimeEventsClient | undefined
    > => {
        if (configuration.mode === 'mock') {
            if (!REALTIME_MOCK_ENABLED) return undefined

            const [
                { createRealtimeEventsMockSharedWorker },
                { installRealtimeEventsMockBridge },
            ] = await Promise.all([
                import('./mock/createRealtimeEventsMockSharedWorker'),
                import('./mock/realtimeEventsMockClient'),
            ])

            if (generation !== clientGeneration) return undefined

            const worker = createRealtimeEventsMockSharedWorker()

            const detachWorkerErrorLogging = installWorkerErrorLogging(
                worker,
                configuration.mode,
            )

            realtimeEventsClient = new BrowserRealtimeEventsClient(
                worker.port,
                configuration.mode,
                installRealtimeEventsMockBridge,
                detachWorkerErrorLogging,
            )

            return realtimeEventsClient
        }

        const { createRealtimeEventsSharedWorker } =
            await import('./createRealtimeEventsSharedWorker')

        if (generation !== clientGeneration) return undefined

        const worker = createRealtimeEventsSharedWorker()

        const detachWorkerErrorLogging = installWorkerErrorLogging(
            worker,
            configuration.mode,
        )

        realtimeEventsClient = new BrowserRealtimeEventsClient(
            worker.port,
            configuration.mode,
            undefined,
            detachWorkerErrorLogging,
        )

        realtimeEventsClient.configureAndConnect(configuration)

        return realtimeEventsClient
    }

    const clientPromise = createClient()
        .then((client) => {
            if (generation !== clientGeneration) return undefined

            return client
        })
        .catch((error: unknown) => {
            logRealtimeTelemetry(
                {
                    event: 'worker_creation_failed',
                    level: 'error',
                    context: { transport: configuration.mode },
                },
                error instanceof Error ? error : undefined,
            )

            return undefined
        })
        .finally(() => {
            if (realtimeEventsClientPromise === clientPromise) {
                realtimeEventsClientPromise = undefined
            }
        })

    realtimeEventsClientPromise = clientPromise

    return clientPromise
}

export const detachRealtimeEventsClient = (
    _context?: RealtimeClientDetachContext,
): void => {
    clientGeneration += 1
    realtimeEventsClient?.detach()
    realtimeEventsClient = undefined
    realtimeEventsClientPromise = undefined
}

export const logoutRealtimeEventsClient = (): void => {
    // Never construct a worker just to log out. Invalidate pending creation
    // and send global logout before cleanup can detach this tab's port.
    clientGeneration += 1
    realtimeEventsClientPromise = undefined
    realtimeEventsClient?.logout()
}
